import { useState } from 'react';
import { Plus, X, FlaskConical, Sprout } from 'lucide-react';
import ChemicalSearch from './ChemicalSearch';
import sprayService from '../../services/spray';
import fertiliserService from '../../services/fertiliser';
import { toNum } from '@/utils/numeric';
import { convertRate } from '@/utils/sprayDose';
import { FORM_LABEL, fertiliserAsMixProduct, gradeText } from '@/utils/fertiliser';
import type { ChemicalWithRegistration } from '../../types/spray';
import type { FertiliserProduct } from '../../types/fertiliser';

/**
 * A product added to the tank alongside the primary one, with the rate it will
 * be dosed at. Kept as the full registration object rather than just an id so
 * the registered range stays available for the in-place rate check — and so the
 * snapshot the server takes has a registration to snapshot.
 */
export interface MixPartner {
  chemical: ChemicalWithRegistration;
  rate: number | '';
  rateUnit: string;
  /** Set for a foliar fertiliser: no registration, so no label range or WHP. */
  fertiliser?: { grade: string };
}


interface MixPartnerEditorProps {
  situationId?: number | null;
  /** The pests the spray is for — partners are narrowed to them too. */
  targetPests?: string[];
  partners: MixPartner[];
  onChange: (partners: MixPartner[]) => void;
  /** Already in the mix (including the primary) — blocks adding a duplicate. */
  excludeChemicalIds: number[];
}

/**
 * Build the extra products in a tank mix.
 *
 * Each partner carries its own rate AND its own rate unit: a mix routinely
 * pairs an L/ha fungicide with a mL/100L miticide, and forcing them to share a
 * unit would make one of them wrong.
 */
function MixPartnerEditor({
  situationId,
  targetPests,
  partners,
  onChange,
  excludeChemicalIds,
}: MixPartnerEditorProps) {
  const [adding, setAdding] = useState(false);
  const [addingFert, setAddingFert] = useState(false);
  const [fertilisers, setFertilisers] = useState<FertiliserProduct[] | null>(null);
  const [duplicate, setDuplicate] = useState<string | null>(null);

  const openFertilisers = async () => {
    setAddingFert(true);
    setAdding(false);
    if (fertilisers) return;
    const r = await fertiliserService.products().catch(() => null);
    // Liquids and soluble powders first: those are what goes in a spray tank.
    const order = (p: FertiliserProduct) => (p.fertiliser_form === 'liquid' || p.fertiliser_form === 'soluble' ? 0 : 1);
    setFertilisers(r?.success && r.data ? [...r.data].sort((a, b) => order(a) - order(b)) : []);
  };

  const addFertiliser = (p: FertiliserProduct) => {
    if (excludeChemicalIds.includes(p.id)) {
      setDuplicate(`${p.product_name} is already in this mix.`);
      return;
    }
    setDuplicate(null);
    onChange([
      ...partners,
      {
        chemical: fertiliserAsMixProduct(p),
        rate: '',
        rateUnit: p.fertiliser_form === 'liquid' ? 'L/ha' : 'kg/ha',
        fertiliser: { grade: gradeText(p.nutrient_analysis) },
      },
    ]);
    setAddingFert(false);
  };

  const add = (chem: ChemicalWithRegistration) => {
    if (excludeChemicalIds.includes(chem.id)) {
      setDuplicate(`${chem.product_name} is already in this mix.`);
      return;
    }
    setDuplicate(null);

    // Seed the rate from the registration's default option so the common case
    // is a confirmation rather than a lookup in the shed.
    const opts = sprayService.effectiveRates(chem);
    const preferred = opts.find(r => r.is_default) ?? opts[0] ?? null;
    const seeded = toNum(preferred?.max_rate) ?? toNum(preferred?.min_rate) ?? '';

    onChange([
      ...partners,
      {
        chemical: chem,
        rate: seeded === null ? '' : seeded,
        rateUnit: preferred?.rate_unit || chem.rate_unit || 'L/ha',
      },
    ]);
    setAdding(false);
  };

  const update = (idx: number, patch: Partial<MixPartner>) => {
    onChange(partners.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  const remove = (idx: number) => {
    onChange(partners.filter((_, i) => i !== idx));
    setDuplicate(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div>
          <h3 className="text-sm font-medium text-neutral-700 flex items-center gap-1.5">
            <FlaskConical className="h-4 w-4 text-primary-600" />
            Tank mix partners
          </h3>
          <p className="text-xs text-neutral-500">
            Other products going into the same tank. Leave empty for a single-product spray.
          </p>
        </div>
        {!adding && !addingFert && (
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              <Plus className="h-4 w-4" />
              Add product
            </button>
            <button
              type="button"
              onClick={openFertilisers}
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              <Sprout className="h-4 w-4" />
              Add foliar fertiliser
            </button>
          </div>
        )}
      </div>

      {partners.map((p, idx) => {
        const opts = sprayService.effectiveRates(p.chemical);
        const min = toNum(opts[0]?.min_rate);
        const max = toNum(opts[0]?.max_rate);
        const labelUnit = opts[0]?.rate_unit || p.chemical.rate_unit || p.rateUnit;
        // The label's range restated in the unit being typed, so 1500 mL/ha is
        // judged against 1–2 L/ha correctly. Not comparable (per ha vs per
        // 100 L) means no verdict rather than a wrong one.
        const inTyped = (v: number | null) => (v == null ? null : convertRate(v, labelUnit, p.rateUnit));
        const minTyped = inTyped(min);
        const maxTyped = inTyped(max);
        const rate = p.rate === '' ? null : Number(p.rate);
        const overMax = rate != null && maxTyped != null && rate > maxTyped;
        const underMin = rate != null && minTyped != null && rate < minTyped;

        return (
          <div key={p.chemical.id} className="rounded-lg border border-neutral-200 bg-white p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-neutral-800 break-words">{p.chemical.product_name}</p>
                {p.fertiliser && (
                  <p className="text-xs text-green-800">Foliar fertiliser · {p.fertiliser.grade} · no label rate to check against</p>
                )}
                {p.chemical.target_pest && (
                  <p className="text-xs text-neutral-500">{p.chemical.target_pest}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => remove(idx)}
                className="text-neutral-400 hover:text-red-600 shrink-0"
                aria-label={`Remove ${p.chemical.product_name} from the mix`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={p.rate}
                onChange={e => update(idx, { rate: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                placeholder={max != null ? String(max) : 'Rate'}
                className="w-28 border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                aria-label={`Rate for ${p.chemical.product_name}`}
              />
              <select
                value={p.rateUnit}
                onChange={e => {
                  // L/ha → mL/ha is the same dose written differently, so the
                  // number moves with it. Per ha → per 100 L is a different
                  // dose and the number is left for the person to restate.
                  const next = e.target.value;
                  const converted = p.rate === '' ? null : convertRate(p.rate, p.rateUnit, next);
                  update(idx, converted == null ? { rateUnit: next } : { rateUnit: next, rate: converted });
                }}
                className="border border-neutral-300 rounded-lg px-2 py-2 text-sm"
                aria-label={`Rate unit for ${p.chemical.product_name}`}
              >
                <option value="L/ha">L/ha</option>
                <option value="mL/ha">mL/ha</option>
                <option value="kg/ha">kg/ha</option>
                <option value="g/ha">g/ha</option>
                <option value="mL/100L">mL/100L</option>
                <option value="g/100L">g/100L</option>
              </select>

              {(min != null || max != null) && (
                <span className="text-xs text-neutral-500">
                  label {min != null ? `${min}–` : 'up to '}{max ?? min} {opts[0]?.rate_unit}
                </span>
              )}
              {overMax && <span className="text-xs text-amber-600 font-medium">above label max</span>}
              {underMin && <span className="text-xs text-amber-600 font-medium">below label min</span>}
            </div>

            {/* WHP is the reason a partner can quietly cost you a harvest
                window, so it is stated here rather than only on the summary. */}
            {(p.chemical.whp_days != null || p.chemical.rei_hours != null) && (
              <p className="text-xs text-amber-700">
                {p.chemical.whp_days != null && <>WHP {p.chemical.whp_days} days</>}
                {p.chemical.whp_days != null && p.chemical.rei_hours != null && ' · '}
                {p.chemical.rei_hours != null && <>REI {p.chemical.rei_hours} h</>}
              </p>
            )}
          </div>
        );
      })}

      {duplicate && <p className="text-xs text-amber-600">{duplicate}</p>}

      {addingFert && (
        <div className="rounded-lg border border-dashed border-neutral-300 p-3 space-y-2">
          {!fertilisers && <p className="text-sm text-neutral-500">Loading your fertilisers…</p>}
          {fertilisers && fertilisers.length === 0 && (
            <p className="text-sm text-neutral-600">
              Your organisation has no fertilisers yet. Add them on Farms → Fertiliser → Products, with their analysis.
            </p>
          )}
          {fertilisers && fertilisers.length > 0 && (
            <ul className="divide-y divide-neutral-100">
              {fertilisers.map((f) => {
                const inMix = excludeChemicalIds.includes(f.id);
                return (
                  <li key={f.id} className="py-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                    <span className="text-sm text-neutral-800 min-w-0 break-words">
                      {f.product_name}
                      <span className="text-xs text-neutral-500"> · {gradeText(f.nutrient_analysis)}{f.fertiliser_form ? ` · ${FORM_LABEL[f.fertiliser_form]}` : ''}</span>
                    </span>
                    <button
                      type="button"
                      disabled={inMix}
                      onClick={() => addFertiliser(f)}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:text-neutral-400"
                    >
                      {inMix ? 'In the mix' : 'Add'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <button
            type="button"
            onClick={() => { setAddingFert(false); setDuplicate(null); }}
            className="text-xs text-neutral-500 hover:text-neutral-700"
          >
            Cancel
          </button>
        </div>
      )}

      {adding && (
        <div className="rounded-lg border border-dashed border-neutral-300 p-3 space-y-2">
          <ChemicalSearch situationId={situationId} targetPests={targetPests} onChange={add} />
          <button
            type="button"
            onClick={() => { setAdding(false); setDuplicate(null); }}
            className="text-xs text-neutral-500 hover:text-neutral-700"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

export default MixPartnerEditor;
